import { useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { chartDate, urlDate } from 'helpers/date';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { percentDiff } from 'helpers/utils';
import { getUserClientId } from 'selectors/user';
import { getUserDates } from 'selectors/viewPreferences';
import { useDonorsData } from 'hooks/useDonorsData';
import { getSummaryData, getSummaryState } from 'selectors/summary';
import { getTotalTransactionData, getTransactionState } from 'selectors/transactions';
import { getDonationState, getTotalDonationData, getTrendDonationData } from 'selectors/donations';
import { getActiveRole } from 'selectors/admins';
import { getTableSummary } from 'store/summary/actions';
import { getTransactionTotal } from 'store/transaction/actions';
import { getDonationTotal, getDonationTrend } from 'store/donation/actions';
import { getPartners } from 'store/partners/actions';
import { chartInitialValues } from 'constants/chart';

export const useDashboardData = () => {
  const dispatch = useAppDispatch();

  const [donationDiff, setDonationDiff] = useState('-');
  const [transactionDiff, setTransactionDiff] = useState('-');
  const [chartData, setChartData] = useState(chartInitialValues);
  const [dimension, setDimension] = useState('recipient');
  const [recentActivityMini, setRecentActivityMini] = useState(false);
  const [showCustomHeader, setShowCustomHeader] = useState(true);

  const clientId = useAppSelector(getUserClientId);
  const userDates = useAppSelector(getUserDates);
  const donationState = useAppSelector(getDonationState);
  const transactionState = useAppSelector(getTransactionState);
  const summaryState = useAppSelector(getSummaryState);
  const totalDonation = useAppSelector(getTotalDonationData);
  const totalTransaction = useAppSelector(getTotalTransactionData);
  const donationTrend = useAppSelector(getTrendDonationData);
  const summary = useAppSelector(getSummaryData);
  const activeRole = useAppSelector(getActiveRole);

  const startDate = userDates?.start ? urlDate(userDates.start, 'start') : undefined;
  const endDate = userDates?.end ? urlDate(userDates.end, 'end') : undefined;

  const { donors, isLoading: donorsLoading } = useDonorsData(
    { start_date: startDate ?? '', end_date: endDate ?? '', client_id: clientId },
    !activeRole || clientId == null || !startDate || !endDate
  );

  const prevParamsRef = useRef<{
    clientID: number | null;
    startDate: string | null;
    endDate: string | null;
    dimension: string;
  }>({ clientID: null, startDate: null, endDate: null, dimension: 'recipient' });

  useEffect(() => {
    // 0 is super's ALL (aggregate across clients); only null means no selection resolved yet.
    if (clientId == null) return;
    dispatch(getPartners({ search_str: clientId, partner_ids: [] }, activeRole));
  }, [dispatch, activeRole, clientId]);

  useEffect(() => {
    if (clientId == null || !startDate || !endDate) return;

    const Today = new Date();
    const Yesterday = new Date().setDate(new Date().getDate() - 1);
    const currentParams = { clientID: clientId, startDate, endDate, dimension };

    const prev = prevParamsRef.current;
    const dateOrClientChanged =
      prev.clientID !== currentParams.clientID ||
      prev.startDate !== currentParams.startDate ||
      prev.endDate !== currentParams.endDate;
    const dimensionChanged = prev.dimension !== currentParams.dimension;

    if (!dateOrClientChanged && !dimensionChanged) return;

    prevParamsRef.current = currentParams;

    if (dateOrClientChanged) {
      batch(() => {
        dispatch(
          getDonationTotal('today', urlDate(Today, 'start'), urlDate(Today, 'end'), clientId)
        );
        dispatch(
          getDonationTotal(
            'yesterday',
            urlDate(Yesterday, 'start'),
            urlDate(Yesterday, 'end'),
            clientId
          )
        );
        dispatch(
          getTransactionTotal('today', urlDate(Today, 'start'), urlDate(Today, 'end'), clientId)
        );
        dispatch(
          getTransactionTotal(
            'yesterday',
            urlDate(Yesterday, 'start'),
            urlDate(Yesterday, 'end'),
            clientId
          )
        );
        dispatch(getDonationTrend(startDate, endDate, clientId));
      });
    }

    dispatch(getTableSummary(startDate, endDate, dimension, clientId));
  }, [clientId, startDate, endDate, dimension, dispatch]);

  useEffect(() => {
    if (totalDonation?.today !== undefined && totalDonation?.yesterday !== undefined) {
      setDonationDiff(percentDiff(totalDonation.today, totalDonation.yesterday));
    }
  }, [totalDonation]);

  useEffect(() => {
    if (totalTransaction?.today !== undefined && totalTransaction?.yesterday !== undefined) {
      setTransactionDiff(percentDiff(totalTransaction.today, totalTransaction.yesterday));
    }
  }, [totalTransaction]);

  useEffect(() => {
    if (donationTrend?.length > 0) {
      setChartData(prev => ({
        ...prev,
        data: {
          ...prev.data,
          labels: donationTrend.map(item => chartDate(item.date)),
          datasets: [
            { ...prev.data.datasets[0], data: donationTrend.map(item => parseInt(item.amount)) },
            ...prev.data.datasets.slice(1),
          ],
        },
      }));
    }
  }, [donationTrend]);

  return {
    donationState,
    transactionState,
    summaryState,
    totalDonation,
    totalTransaction,
    donationTrend,
    summary,
    donors,
    donorsLoading,
    donationDiff,
    transactionDiff,
    chartData,
    dimension,
    setDimension,
    recentActivityMini,
    setRecentActivityMini,
    showCustomHeader,
    setShowCustomHeader,
  };
};
